import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import cn from 'clsx'

import { Watchlist } from '@App/redux/slices/watchlist/slice'
import { DragIcon } from '@App/static/icons/MarketPanelIcons'
import { PlusWithBGIcon } from '@App/static/icons/SpotOrderIcons'
import {
  WatchlistItemRow,
  WatchListItemsContainer,
} from '@App/ui/crypto_exchange/desktop/pages/spot/components/SpotOrderMarketsTable/components/SpotOrderMarketsTableSideBar/WatchlistItem'
import { Switch } from '@App/ui/crypto_exchange/desktop/pages/spot/components/SpotOrderTrade/components/Switch'

import { Button } from '../../../../controls/Button'

import s from './styles.module.scss'

const FIXED_WATCHLIST_ID = 'portfolio'

type SortableWatchlistItemProps = {
  watchlist: Watchlist
  isActive: boolean
  onSelect: () => void
  onToggleActive: (checked: boolean) => void
}

const SortableWatchlistItem = ({ watchlist, isActive, onSelect, onToggleActive }: SortableWatchlistItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: watchlist.key })

  const style = {
    transform: CSS.Transform.toString(transform ? { ...transform, x: 0 } : null),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <WatchlistItemRow
        item={watchlist}
        onClick={onSelect}
        className={s.listItem}
        isActive={isActive}
        actions={
          <div className={cn(s.actions, isActive && s.visible)}>
            {!watchlist.isCustom && <Switch checked={watchlist.is_enabled} onChange={onToggleActive} />}
            <div className={s.dragHandle} {...attributes} {...listeners}>
              <DragIcon className={s.dragIcon} />
            </div>
          </div>
        }
      />
    </div>
  )
}

const FixedWatchlistItem = ({
  watchlist,
  onToggleActive,
}: Omit<SortableWatchlistItemProps, 'onSelect' | 'isActive'>) => {
  return (
    <WatchlistItemRow
      item={watchlist}
      className={s.listItem}
      actions={
        <div className={s.actions}>
          <Switch checked={watchlist.is_enabled} onChange={onToggleActive} />
        </div>
      }
    />
  )
}

type WatchlistsSidebarProps = {
  watchlists: Watchlist[]
  activeWatchlistId: string | null
  onSelect: (id: string) => void
  onToggleActive: (id: string, checked: boolean) => void
  onReorder: (newOrder: string[]) => void
  onCreateNew: () => void
}

export const WatchlistsSidebar = ({
  watchlists,
  activeWatchlistId,
  onSelect,
  onToggleActive,
  onReorder,
  onCreateNew,
}: WatchlistsSidebarProps) => {
  const portfolioWatchlist = watchlists.find((w) => w.key === FIXED_WATCHLIST_ID)
  const sortableWatchlists = watchlists.filter((w) => w.key !== FIXED_WATCHLIST_ID)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = sortableWatchlists.findIndex((w) => w.key === active.id)
      const newIndex = sortableWatchlists.findIndex((w) => w.key === over.id)

      const reorderedSortable = arrayMove(
        sortableWatchlists.map((w) => w.key),
        oldIndex,
        newIndex,
      )

      const newOrder = [FIXED_WATCHLIST_ID, ...reorderedSortable]
      onReorder(newOrder)
    }
  }

  return (
    <div className={s.container}>
      <div className={s.title}>Watchlists</div>
      <div className={s.list}>
        <WatchListItemsContainer>
          {portfolioWatchlist && (
            <FixedWatchlistItem
              watchlist={portfolioWatchlist}
              onToggleActive={(checked) => onToggleActive(portfolioWatchlist.key, checked)}
            />
          )}

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortableWatchlists.map((w) => w.key)} strategy={verticalListSortingStrategy}>
              {sortableWatchlists.map((watchlist) => (
                <SortableWatchlistItem
                  key={watchlist.key}
                  watchlist={watchlist}
                  isActive={activeWatchlistId === watchlist.key}
                  onSelect={() => onSelect(watchlist.key)}
                  onToggleActive={(checked) => onToggleActive(watchlist.key, checked)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </WatchListItemsContainer>
      </div>
      <div className={s.add}>
        <Button className={cn(s.btn, s.small)} variant="outlined" onClick={onCreateNew}>
          <PlusWithBGIcon className={s.plusIcon} /> Create new watchlist
        </Button>
      </div>
    </div>
  )
}
